import * as React from "react";
import * as globals from "../../../globals";
import * as modelsGet from "../../../models/get";
import * as modelsPost from "../../../models/post";
import * as modelsForm from "../../../models/form";
import * as utils from "../../../utils/utils";
import * as utilsViewMode from "../../../utils/viewmode";
import * as apiAccounts from "../../../api/accounts";
import * as apiSettings from "../../../api/settings";
import SettingsNav from "../nav";
import { AlertType, Alert } from "../../../components/alert";
import { CalendarViewMode } from "../../../enumerations";
import { Loader, LoaderStyle } from "../../../components/loader";
import { ScrollViewer, ScrollBarVisibility } from "../../../components/scrollViewer";
import { GroupBox } from "../../../components/groupBox";
import { FormGroup } from "../../../components/form";
import { TextBox } from "../../../components/textBox";
import { InputSize, InputSideButton } from "../../../components/input";
import { ComboBox, DROPDOWN_TEXT_ITEM_HEIGHT, ComboBoxType, DropDownTextItem } from "../../../components/comboBox";
import { CheckBox } from "../../../components/checkbox";
import { Button, ButtonType } from "../../../components/button";
import { ImageDialog } from "./imageDialog";

interface IViewMode {
    id?: number,
    key?: string,
    mode?: string
}

interface IKeyValue
{
    key: string,
    value: string
}

interface IState
{
    /**
     * Gibt an, ob Daten gerade geladen werden.
     */
    isLoading?: boolean;

    /**
     * Das momentan selektierte TabItem.
     */
    selectedTabIndex?: number;

    /**
     * Enthält den geöffneten Datensatz.
     */
    user?: modelsGet.IUser;

    /**
     * Enthält alle Zeitzonen.
     */
    timezones?: modelsGet.ITimezone[];

    /**
     * Enthält alle Kalenderansichten.
     */
    viewModes?: IViewMode[];

    /**
     * Enthält die Eingabedaten des Forms.
     */
    form?: {
        // Allgemein
        firstname?: string;
        lastname?: string;
        timezone?: modelsForm.IComboBoxValue<string, string>;

        // Darstellung
        hideUpcomingEvents?: boolean;
        defaultCalendarView?: modelsForm.IComboBoxValue<string, string>;
    },

    /**
     * Gibt an, ob der Dialog zum ändern des Profilbilds geöffnet ist.
     */
    isImageDialogOpen?: boolean;

    /**
     * Enthält Serverseitige Fehlermeldungen.
     */
    errors?: Object;

    /**
     * Der Text der Meldungen welche angezeigt werden soll.
     */
    alertMessage?: string;

    /**
     * Der Typ der Meldung.
     */
    alertType?: AlertType;
}

export class UserProfileGeneral extends React.Component<{}, IState>
{
    constructor(props: any)
    {
        super(props);

        this.state = {
            isLoading: true,
            form: {
                timezone: {},
                defaultCalendarView: {}
            },
            errors: {}
        };

        this.initAsync();
    }

    /**
     * Lädt alle benötigten Daten und/oder weißt diese zu.
     * @param props Alle gesetzten Eigenschaften.
     */
    private async initAsync()
    {
        var user: modelsGet.IUser = await apiAccounts.getCurrentUser();
        var timezones: modelsGet.ITimezone[] = await apiSettings.getTimezones();

        this.setState((state: IState) => {
            state.isLoading = false;
            state.errors = {};
            state.alertMessage = null;
            state.alertType = null;
            state.user = user;
            state.timezones = timezones;
            state.viewModes = [
                {
                    id: CalendarViewMode.day,
                    key: utilsViewMode.getViewModeString(CalendarViewMode.day),
                    mode: "Tag"
                },
                {
                    id: CalendarViewMode.workWeek,
                    key: utilsViewMode.getViewModeString(CalendarViewMode.workWeek),
                    mode: "Arbeitswoche"
                },
                {
                    id: CalendarViewMode.week,
                    key: utilsViewMode.getViewModeString(CalendarViewMode.week),
                    mode: "Woche"
                },
                {
                    id: CalendarViewMode.month,
                    key: utilsViewMode.getViewModeString(CalendarViewMode.month),
                    mode: "Monat"
                },
                {
                    id: CalendarViewMode.planning,
                    key: utilsViewMode.getViewModeString(CalendarViewMode.planning),
                    mode: "Planungsansicht"
                }
            ];

            state.form.firstname = user.firstname;
            state.form.lastname = user.lastname;
            state.form.hideUpcomingEvents = user.settings.style.hideUpcomingEvents;

            let timezone: modelsGet.ITimezone = timezones.where(t => t.key === user.settings.timezone).first();
            state.form.timezone = {
                selectedValue: timezone.key,
                value: timezone.text
            };

            let viewMode: IViewMode = state.viewModes.where(t => t.key === user.settings.style.defaultCalendarView).first();
            if (viewMode != null) {
                state.form.defaultCalendarView = {
                    selectedValue: viewMode.key,
                    value: viewMode.mode
                };
            }

            return state;
        });
    }

    private onSaveClick = async () =>
    {
        try
        {
            this.setState({
                isLoading: true
            });

            const { form } = this.state;
            const newData: modelsPost.IUserProfile = {
                firstname: form.firstname,
                lastname: form.lastname,
                timezone: form.timezone.selectedValue,
                style: {
                    hideUpcomingEvents: form.hideUpcomingEvents,
                    defaultCalendarView: form.defaultCalendarView.selectedValue
                }
            };

            await apiAccounts.changeUser(newData);
            await globals.getCurrentUserAsync(true);
            await globals.getUsersAsync(true);

            this.setState({
                isLoading: false,
                errors: {},
                alertMessage: null
            });
        }
        catch (e)
        {
            var errors = utils.getErrors(e);
            var alertMessage: string;

            if (errors != null)
            {
                for (let error in errors)
                {
                    switch (error)
                    {
                        case "error":
                        case "date_is_invalid":
                            alertMessage = errors[error];
                            break;
                        case "internal_server_error":
                            alertMessage = "Auf dem Server ist ein Fehler aufgetreten! Wenn der Fehler weiterhin auftritt, kontaktieren Sie Ihren Administrator oder Ihren Software Anbieter.";
                            break;
                    }
                }
            }

            this.setState(() =>
            {
                const newState: IState = {};
                newState.isLoading = false;
                newState.errors = errors;
                newState.alertMessage = alertMessage;
                newState.alertType = AlertType.error;
                return newState;
            });
        }
    }
    
    /**
     * Wird ausgeführt, sobald sich der Wert eines Controls ändert.
     * @param value Der konvertierte Wert der TextBox.
     * @param originalValue Der originale Wert der TextBox.
     * @param name Der Name des Form Elements.
     */
    private onControlTextChanged = (value: any[], originalValue: string[], name: string) => {
        this.setState((state: IState) => {
            state.form[name] = value[0];
            return state;
        });
    }

    /**
     * Wird ausgeführt, sobald sich der Wert einer ComboBox ändert.
     * @param selectedItem Das neu selektierte Item.
     * @param valueName Der Property Name für den "value" Wert.
     * @param selectedValueName Der Property Name für den "selectedValue" Wert.
     * @param formItemName Der Name des Form Elements.
     */
    private onComboBoxChanged = (selectedItem: any, valueName: string, selectedValueName: string, formItemName: string) => {
        this.setState((state: IState) => {
            state.form[formItemName] = {
                value: selectedItem && selectedItem[valueName],
                selectedValue: selectedItem && selectedItem[selectedValueName]
            };
            return state;
        });
    }

    /**
     * Wird ausgeführt, sobald sich der Wert einer CheckBox ändert.
     * @param e Das auslösende Ereignis.
     */
    private onCheckboxChanged = (e: React.ChangeEvent<HTMLInputElement>, name: string) => {
        var target = e.target;
        var value = target.checked;

        this.setState((state: IState) => {
            state.form[name] = value;
            return state;
        });
    }

    /**
     * Öffnet den Dialog zum ändern des Profilbilds.
     */
    private onImageEditClick = (): void => {
        this.setState({
            isImageDialogOpen: true
        });
    }

    /**
     * Wird aufgerufen, sobald sich der Dialog schließt.
     */
    private onDialogClosed = (refresh?: boolean) => {
        globals.getCurrentUserAsync().then((user) => {
            this.setState((state: IState) => {
                state.isImageDialogOpen = false;
                state.user = user;
                return state;
            });
        });
    }

    /**
     * Rendert diese Komponente.
     */
    public render(): JSX.Element
    {
        var {
            isLoading,
            timezones,
            viewModes,
            user,
            form,
            errors,
            isImageDialogOpen,
            alertMessage,
            alertType
        } = this.state;

        if (isLoading)
        {
            return (
                <Loader
                    style={{ marginTop: 40 }}
                    loaderStyle={LoaderStyle.style1} />
            );
        }

        const isAdmin: boolean = user.group_name === "Administrator";

        return (
            <div className="settings-content">
                <div className="settings-content_container">
                    <div className="settings-content_nav">
                        <SettingsNav selected="profile" />
                    </div>
                    <div className="settings-content_alert">
                        <Alert
                            alertType={alertType}
                            isClosable={true}
                            isVisible={alertMessage != null}
                            onHide={() => this.setState({ alertMessage: null })}>
                            {alertMessage}
                        </Alert>
                    </div>
                    <div className="settings-others">
                        <div className="settings-others_content-wrapper">
                            <ScrollViewer
                                HorizontalScrollBarVisibility={ScrollBarVisibility.hidden}
                                VerticalScrollBarVisibility={ScrollBarVisibility.auto}>
                                <div className="settings-others_content">
                                    <GroupBox>
                                        <div className="profile-image">
                                            <div
                                                className="profile-image_overlay"
                                                onClick={this.onImageEditClick}>
                                                <img src={user.avatar} />
                                                <i className="mdi mdi-pencil"></i>
                                            </div>
                                        </div>
                                        <FormGroup>
                                            <TextBox
                                                label="Vorname"
                                                value={form.firstname}
                                                size={InputSize.normal}
                                                spellCheck={true}
                                                autoFocus={true}
                                                errorMessage={errors["Firstname"]}
                                                onTextChange={(value, originalValue) => this.onControlTextChanged(value, originalValue, "firstname")} />
                                            <TextBox
                                                label="Nachname"
                                                value={form.lastname}
                                                size={InputSize.normal}
                                                spellCheck={true}
                                                errorMessage={errors["Lastname"]}
                                                onTextChange={(value, originalValue) => this.onControlTextChanged(value, originalValue, "lastname")} />
                                            <ComboBox
                                                label="Zeitzone"
                                                dataSource={timezones}
                                                dropDownMenuItemHeight={DROPDOWN_TEXT_ITEM_HEIGHT}
                                                comboBoxType={ComboBoxType.dropDownList}
                                                value={form.timezone.value}
                                                selectedValue={form.timezone.selectedValue}
                                                onSelectionChanged={(data) => this.onComboBoxChanged(data, "text", "key", "timezone")}
                                                valueMember="key"
                                                displayMember="text">
                                                {
                                                    (item: modelsGet.ITimezone) => {
                                                        return (
                                                            <DropDownTextItem
                                                                key={item.key}
                                                                value={item.key}
                                                                displayValue={item.text} />
                                                        );
                                                    }
                                                }
                                            </ComboBox>
                                        </FormGroup>
                                    </GroupBox>
                                    <GroupBox headerText="Darstellung">
                                        <FormGroup>
                                            <ComboBox
                                                canRemoveSelection={true}
                                                label="Kalenderansicht die bei jedem Erststart dargestellt wird"
                                                dataSource={viewModes.where(v => user.claims.forbidden_view_modes && !user.claims.forbidden_view_modes.contains(v.id))}
                                                dropDownMenuItemHeight={DROPDOWN_TEXT_ITEM_HEIGHT}
                                                comboBoxType={ComboBoxType.dropDownList}
                                                value={form.defaultCalendarView.value}
                                                selectedValue={form.defaultCalendarView.selectedValue}
                                                onSelectionChanged={(data) => this.onComboBoxChanged(data, "mode", "key", "defaultCalendarView")}
                                                valueMember="key"
                                                displayMember="mode"
                                                hint="Diese Auswahl überschreibt die Funktion, welche die zuletzt ausgewählte Kalenderansicht speichert."
                                                sideButtons={[
                                                    <InputSideButton
                                                        icon="close"
                                                        isDisabled={form.defaultCalendarView == null || form.defaultCalendarView.selectedValue == null}
                                                        title="Selektion entfernen"
                                                        onClick={
                                                            () => {
                                                                this.setState((state: IState) => {
                                                                    state.form.defaultCalendarView = {};
                                                                    return state;
                                                                });
                                                            }
                                                        } />
                                                ]}>
                                                {
                                                    (item: IViewMode) => {
                                                        return (
                                                            <DropDownTextItem
                                                                key={item.key}
                                                                value={item.key}
                                                                displayValue={item.mode} />
                                                        );
                                                    }
                                                }
                                            </ComboBox>
                                            <CheckBox
                                                style={{
                                                    marginTop: 10
                                                }}
                                                label="Anzeige der bevorstehenden Termine ausblenden"
                                                isChecked={form.hideUpcomingEvents}
                                                onCheckedChanged={(e) => this.onCheckboxChanged(e, "hideUpcomingEvents")} />
                                        </FormGroup>
                                    </GroupBox>
                                </div>
                            </ScrollViewer>
                        </div>
                        <div className="settings-others_actions">
                            <Button
                                text="Speichern"
                                type={ButtonType.primary}
                                onClick={this.onSaveClick} />
                        </div>
                    </div>
                </div>

                {
                    isImageDialogOpen &&
                    <ImageDialog onClose={this.onDialogClosed} />
                }
            </div>
        );
    }
}